/**
 * How vote forecasts work (/dashboard/forecast-method; §2.8, §2.9, screenshots/45-how-forecasts-work.png).
 * A plain-language article linked from every "How forecasts work" / "How this forecast works". The sample
 * forecast is computed live by forecastVote() from the inputs it describes (§8); the accuracy figures come
 * from the published backtest (snapshot:forecast-method, worker/src/legislation.js).
 */
import { useEffect, useState } from "react";
import { Activity, ArrowLeft, Banknote, FileText, History, Scale, X } from "lucide-react";
import { Face } from "../components/shared/Face";
import { ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { councilTension, forecastVote } from "../forecast/forecast";
import { PRIOR_MODEL } from "../forecast/model";
import { modelLine } from "../legislation/LegislationList";
import { ForecastChip, ForecastMeter, pct } from "../forecast/ForecastViews";
import { accuracySummary } from "../legislation/logic";
import { AiChip, LinkArrow } from "../legislation/ui";
import type { ListData } from "../legislation/types";
import { Crumbs, sectionCrumb } from "../app/crumbs";

const SECTIONS = [
  ["looks", "What a forecast looks like"], ["inputs", "What goes into it"], ["accuracy", "How accurate it's been"],
  ["not", "What a forecast is not"], ["sources", "Where the data comes from"],
] as const;

// The sample: the worked example from the model (Nugent × CAA), computed, never typed in. Always the hand prior, so the
// worked example never moves when a learned model is promoted.
const SAMPLE = forecastVote({ position: "oppose", similar: { yesRate: 0.28, n: 8 }, moneyLinked: { k: 2, n: 2 }, memberLean: 0.32, billLean: -0.55,
  tension: councilTension([{ yea: 30, nay: 18 }, { yea: 46, nay: 4 }, { yea: 48, nay: 1 }, { yea: 44, nay: 5 }, { yea: 47, nay: 1 }]), councilYesRate: 0.7 }, PRIOR_MODEL);

export function ForecastMethod({ accuracy }: { accuracy: ListData["accuracy"] | null }) {
  const [active, setActive] = useState<string>("looks");
  useEffect(() => {
    const io = new IntersectionObserver((es) => {
      const top = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (top) setActive(top.target.id);
    }, { rootMargin: "-20% 0px -60% 0px" });
    SECTIONS.forEach(([id]) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);
  const s = accuracy ? accuracySummary(accuracy.tests) : null;
  const near70 = accuracy?.calibration.length ? accuracy.calibration.reduce((a, x) => (Math.abs(x.expected - 0.7) < Math.abs(a.expected - 0.7) ? x : a)) : null;
  const inputs = [
    [History, "1. Their record on similar items", "How they voted on the most similar past items. Every forecast starts here."],
    [Banknote, "2. Money-linked votes with the lobbyist", "Past votes where the lobbying client stated a position and the member had received money from that client in the year before. One or two votes barely count. It takes several before this moves the forecast. Inferred positions never count here."],
    [Scale, "3. Leaning", "How close the member's leaning is to the bill's. This counts for more when the council is split."],
    [Activity, "4. Council tension", "How divided the council has been on this topic. When members usually vote together, forecasts lean toward the usual outcome. When they're split, the range gets wider."],
    // Copy pending designer review: the learned factors (forecast/model.ts).
    [History, "5. Other evidence, weighed by what it has predicted so far", "Whether the member sponsors the item, what the committee decided, what they said about it on the record, how they vote on Mayor-sponsored items, where their closest colleagues stand, what verified residents of their ward said, money from clients with a stated position, and their place in the relationship graph. Each starts at zero weight and earns one only as it proves to predict real votes."],
  ] as const;
  return (
    <div className="page lg-method">
      <Crumbs trail={[sectionCrumb("legislation"), { label: "How forecasts work" }]} />
      <div className="lg-method__cols">
        <article className="lg-method__main">
          <header className="lg-method__head">
            <AiChip>Forecast</AiChip>
            <h1>How vote forecasts work</h1>
            <p>A forecast is our best estimate of how a member will vote, and how sure we are. It's built from public records, shown with its reasons, and checked against every real vote. It is never a statement of intent.</p>
          </header>

          <section id="looks">
            <h2>What a forecast looks like</h2>
            <p>Every forecast shows a percentage, a range and a confidence level. The range matters as much as the number: a wide range means we know less.</p>
            <div className="lg-sample">
              <ListRow as="div" className="member-row lg-sample__row">
                <RowLead><Face name="Samantha Nugent" size={30} /></RowLead>
                <RowMain min={140} className="member-row__who"><RowTitle><b>Samantha Nugent</b></RowTitle><RowSub><small>Ward 39 · Moderate · 2 of 2 money-linked their way</small></RowSub></RowMain>
                <RowCell><ForecastMeter f={SAMPLE} perspective="their" /></RowCell>
                <RowCell width={190} hide="phone"><ForecastChip f={SAMPLE} perspective="their" showPct={false} /></RowCell>
              </ListRow>
              <dl className="lg-sample__legend">
                <div><dt>Marker</dt><dd>Our estimate: {pct(SAMPLE.p)}%</dd></div>
                <div><dt>Shaded band</dt><dd>Range: {pct(SAMPLE.low)}–{pct(SAMPLE.high)}%</dd></div>
                <div><dt>Chip</dt><dd>Likely · Toss-up · Unlikely</dd></div>
              </dl>
              <small>Sample forecast</small>
            </div>
          </section>

          <section id="inputs">
            <h2>What goes into it</h2>
            <p>Four things, weighed by how much evidence there is.</p>
            <ol className="lg-inputs">{inputs.map(([Icon, title, body]) => (
              <li key={title}><span className="lg-inputs__icon"><Icon size={18} aria-hidden /></span><div><b>{title}</b><p>{body}</p></div></li>
            ))}</ol>
          </section>

          <section id="accuracy">
            <h2>How accurate it's been</h2>
            <p>We freeze each forecast the day before the vote and compare it with the result. Every miss is published, including the ones we got wrong.</p>
            {s && s.total > 0 && (
              <div className="stat-row lg-method__stats">
                <div className="stat"><b>{s.called} of {s.total}</b><span>outcomes called right</span></div>
                <div className="stat"><b>±{s.medianMiss} votes</b><span>median miss</span></div>
                {near70 && <div className="stat"><b>{Math.round(near70.actual * 100)}%</b><span>happened when we said {Math.round(near70.expected * 10) * 10}%</span></div>}
              </div>
            )}
            {accuracy?.model && <p className="lg-muted-14">{modelLine(accuracy.model, accuracy.frozen)}</p>}
            <LinkArrow href="/dashboard/legislation?tab=accuracy">See every forecast vs. result</LinkArrow>
          </section>

          <section id="not">
            <h2>What a forecast is not</h2>
            <ul className="lg-not">
              <li><X size={16} aria-hidden />Not a statement of how anyone intends to vote. Members decide; we estimate.</li>
              <li><X size={16} aria-hidden />Not an accusation. Money near a vote is not evidence that it influenced the vote. We say "voted their way 2 of 2 times after receiving money", and nothing more.</li>
              <li><X size={16} aria-hidden />Not final. Forecasts change as members speak, file, receive money or vote on similar items. The reason is shown each time.</li>
            </ul>
          </section>

          <section id="sources">
            <h2>Where the data comes from</h2>
            <p>City Clerk records (legislation, roll calls), City Council and committee video (what members said), Illinois State Board of Elections and the Board of Ethics (contributions, lobbying filings), refreshed daily. Donors and lobbyists are matched by name.</p>
          </section>
        </article>

        <aside className="lg-method__side">
          <nav className="card lg-toc" aria-label="On this page">
            <span className="eyebrow lg-kicker">On this page</span>
            {SECTIONS.map(([id, label]) => <a key={id} href={`#${id}`} aria-current={active === id ? "location" : undefined}>{label}</a>)}
          </nav>
          <section className="lg-note-box lg-wrong">
            <strong>Think a forecast is wrong?</strong>
            <p>Use "Flag as inaccurate" on any forecast. A person reviews every flag, and members can add context to their own record.</p>
            <LinkArrow href="/methods/vote-forecast.pdf" icon={<FileText size={14} aria-hidden />}>Technical method (PDF)</LinkArrow>
          </section>
        </aside>
      </div>
    </div>
  );
}
